import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { getAppEnv } from '@/config/env';
import { refreshToken } from '@/services/ant-design-pro/api';
import { destroyIMClient } from '@/services/im';
import {
  completeInflightRequest,
  isAuthAllowlistRequest,
  isLogoutDraining,
  LogoutDrainingError,
  registerInflightRequest,
} from '@/utils/authSessionManager';
import { createLogger, isApiLogContext } from '@/utils/logger';
import {
  clearTokens,
  getAccessToken,
  getAuthUserId,
  getRefreshToken,
  isIdleTimeout,
  isTokenExpired,
  setTokens,
  shouldAutoRefresh,
  updateLastActiveTime,
} from '@/utils/token';

const logger = createLogger('RequestErrorConfig');
const apiLogger = createLogger('APIRequest');

// 用于防止刷新token时的并发请求
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let refreshStartTime: number | null = null;
const REFRESH_TIMEOUT = 10000; // 10秒超时
let isReloadScheduled = false; // 防止多次触发 reload
let fallbackRequestSeq = 0;

// 创建一个全局的 message 和 notification 实例
// 在非组件环境中，我们使用静态 API，虽然会显示警告但不影响功能
// 为了减少警告，我们创建一个包装函数
// 使用 any 类型避免类型检查问题，因为这些实例在运行时是有效的
let globalMessage: any = null;
let globalNotification: any = null;

// 初始化全局实例（在 app.tsx 中调用）
export function initGlobalMessageInstances(
  messageInstance: any,
  notificationInstance: any,
) {
  globalMessage = messageInstance;
  globalNotification = notificationInstance;
}

// 获取 message 实例
const getMessageInstance = () => {
  // 如果全局实例存在，使用它；否则使用静态 API（会有警告但功能正常）
  return globalMessage || message;
};

// 获取 notification 实例
const getNotificationInstance = () => {
  return globalNotification || notification;
};

/**
 * 会话失效（被动登出）统一清理：
 * - 销毁全局 IM 实例（登录态已失效，保留旧实例只会继续报错）
 * - 清 token
 * - 弹提示
 * - 延迟跳登录页
 *
 * 401 路径上的所有清理点都应走这里，不要再自行散落调用。
 * IM destroy 不等待：即便 logout 慢或失败也不应阻塞跳登录页。
 * 多个并发请求同时失败时只执行一次。
 */
let isSessionCleanupDone = false;
function sessionExpiredCleanup(userMessage: string): void {
  if (isSessionCleanupDone) return;
  isSessionCleanupDone = true;
  void destroyIMClient();
  clearTokens();
  getMessageInstance().error(userMessage);
  setTimeout(() => {
    history.push('/user/login');
  }, 1000);
}

export function resetSessionCleanupState(): void {
  isSessionCleanupDone = false;
}

/**
 * 重置刷新状态
 */
export function resetRefreshState(): void {
  isRefreshing = false;
  refreshPromise = null;
  refreshStartTime = null;
  isReloadScheduled = false;
}

/**
 * 检查并重置异常状态
 */
function checkAndResetState(): void {
  // 检查超时
  if (
    isRefreshing &&
    refreshStartTime &&
    Date.now() - refreshStartTime > REFRESH_TIMEOUT
  ) {
    logger.warn('刷新token超时，重置状态');
    resetRefreshState();
    return;
  }

  // 如果状态异常（比如刷新标志为true但没有promise），重置状态
  if (isRefreshing && !refreshPromise) {
    logger.warn('检测到异常状态，重置刷新状态');
    resetRefreshState();
  }
}

// HTTP 状态码到用户友好提示的映射（后端没给业务文案时兜底使用）
const STATUS_MESSAGE_MAP: Record<number, string> = {
  400: '请求参数错误',
  403: '没有访问权限',
  408: '请求超时，请稍后重试',
  500: '服务器内部错误，请稍后重试',
  502: '网关错误，请稍后重试',
  503: '服务暂不可用，请稍后重试',
  504: '网关超时，请稍后重试',
};

// 从后端响应体中提取业务错误信息，兼容多种常见字段命名
function extractBackendErrorMessage(response: any): string | null {
  const data = response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  return (
    data.errorMessage ||
    data.message ||
    data.msg ||
    data.error ||
    data.detail ||
    null
  );
}

function getBrowserPagePath(): string {
  const location =
    history?.location ||
    (typeof window !== 'undefined' ? window.location : undefined);
  if (!location) return '';
  return `${location.pathname || ''}${location.search || ''}`;
}

function safeLocalStorageGet(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function normalizeRequestUrl(url?: unknown): string {
  if (!url) return '';
  const rawUrl = String(url);
  try {
    const parsed = new URL(
      rawUrl,
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost',
    );
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return rawUrl;
  }
}

function getSearchParam(rawUrl: string, names: string[]): string | undefined {
  if (!rawUrl) return undefined;
  try {
    const parsed = new URL(
      rawUrl,
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost',
    );
    for (const name of names) {
      const value = parsed.searchParams.get(name);
      if (value) return value;
    }
  } catch {
    // ignore invalid URLs; other extraction strategies still apply
  }
  return undefined;
}

function getObjectParam(
  params: Record<string, any> | undefined,
  names: string[],
): string | undefined {
  if (!params || typeof params !== 'object') return undefined;
  for (const name of names) {
    const value = params[name];
    if (typeof value === 'string' || typeof value === 'number') {
      const normalized = String(value);
      if (normalized) return normalized;
    }
  }
  return undefined;
}

function getPathParam(
  rawUrl: string,
  segmentNames: string[],
): string | undefined {
  if (!rawUrl) return undefined;
  const path = normalizeRequestUrl(rawUrl).split('?')[0] || '';
  for (const segmentName of segmentNames) {
    const match = path.match(new RegExp(`/${segmentName}/([^/?#]+)`));
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return undefined;
}

function inferRequestModule(url: string, pagePath: string): string {
  if (pagePath.startsWith('/patient') || url.includes('/patients')) {
    return 'Patient';
  }
  if (url.includes('/members/') || url.includes('/health-data')) {
    return 'Patient';
  }
  if (pagePath.includes('/project-admin')) return 'ProjectAdmin';
  if (pagePath.includes('/org-admin')) return 'OrgAdmin';
  if (pagePath.includes('/super-admin')) return 'SuperAdmin';
  if (pagePath.startsWith('/knowledge') || url.includes('/knowledge')) {
    return 'Knowledge';
  }
  if (pagePath.startsWith('/user/login') || url.includes('/v1/auth')) {
    return 'Auth';
  }
  if (url.includes('/v1/im')) return 'IM';
  return 'APIRequest';
}

function compactContext(context: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(context).filter(
      ([, value]) => value !== undefined && value !== '',
    ),
  );
}

function normalizeExplicitContextValue(key: string, value: unknown): unknown {
  if (value === null || value === undefined || value === '') return undefined;
  if (
    key === 'projectId' ||
    key === 'patientId' ||
    key === 'userId' ||
    key.endsWith('_id')
  ) {
    return String(value);
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  return String(value);
}

function getExplicitApiLogContext(
  config: any,
  opts?: any,
): Record<string, any> {
  const explicitContext = config?.__logContext ?? opts?.__logContext;
  if (
    !explicitContext ||
    typeof explicitContext !== 'object' ||
    Array.isArray(explicitContext)
  ) {
    return {};
  }

  return compactContext(
    Object.fromEntries(
      Object.entries(explicitContext).map(([key, value]) => [
        key,
        normalizeExplicitContextValue(key, value),
      ]),
    ),
  );
}

function buildApiLogContext(
  config: any,
  opts?: any,
  status?: number,
  errorMessage?: string | null,
): Record<string, any> {
  const rawUrl = config?.url ?? opts?.url;
  const url = normalizeRequestUrl(rawUrl);
  const method = String(config?.method ?? opts?.method ?? 'GET').toUpperCase();
  const pagePath = getBrowserPagePath();
  const params = (config?.params ?? opts?.params) as
    | Record<string, any>
    | undefined;
  const requestId =
    Number(config?.__requestId ?? opts?.__requestId ?? 0) || undefined;
  const startedAt = Number(
    config?.__requestStartedAt ?? opts?.__requestStartedAt ?? 0,
  );
  const duration = startedAt ? Date.now() - startedAt : undefined;
  const projectId =
    getObjectParam(params, ['projectId', 'project_id']) ||
    getSearchParam(url, ['projectId', 'project_id']) ||
    getPathParam(url, ['projects']) ||
    getSearchParam(pagePath, ['projectId', 'project_id']) ||
    (pagePath.startsWith('/patient') || pagePath.includes('/project-admin')
      ? safeLocalStorageGet('selectedProjectId') || undefined
      : undefined);
  const patientId =
    getObjectParam(params, [
      'patientId',
      'patient_id',
      'memberId',
      'member_id',
    ]) ||
    getSearchParam(url, ['patientId', 'patient_id', 'memberId', 'member_id']) ||
    getPathParam(url, ['patients', 'members']) ||
    getSearchParam(pagePath, [
      'patientId',
      'patient_id',
      'memberId',
      'member_id',
    ]);
  const explicitContext = getExplicitApiLogContext(config, opts);

  return compactContext({
    api: `${method} ${url}`,
    method,
    url,
    status,
    duration,
    requestId,
    environment: getAppEnv(),
    userId: getAuthUserId() || undefined,
    pagePath,
    projectId,
    patientId,
    module: inferRequestModule(url, pagePath),
    errorMessage: errorMessage || undefined,
    ...explicitContext,
  });
}

const WRITABLE_API_FIELDS = [
  'api',
  'method',
  'url',
  'httpStatus',
  'outcome',
  'duration',
  'requestId',
  'pagePath',
  'errorMessage',
] as const;

function attachApiInfoToContext(
  config: any,
  apiContext: Record<string, any>,
  outcome: 'success' | 'failure',
): void {
  const target = config?.__logContext;
  if (!isApiLogContext(target)) return;
  const writable: Record<string, unknown> = {
    api: apiContext.api,
    method: apiContext.method,
    url: apiContext.url,
    httpStatus: apiContext.status,
    outcome,
    duration: apiContext.duration,
    requestId: apiContext.requestId,
    pagePath: apiContext.pagePath,
    errorMessage: apiContext.errorMessage,
  };
  for (const key of WRITABLE_API_FIELDS) {
    const value = writable[key];
    if (value !== undefined && value !== '') {
      (target as Record<string, unknown>)[key] = value;
    }
  }
}

function getErrorLogConfig(error: any, opts?: any): any {
  return error?.config || error?.response?.config || opts || {};
}

function hasApiResultLogged(config: any): boolean {
  return Boolean(config?.__apiResultLogged);
}

function markApiResultLogged(config: any): void {
  if (config && typeof config === 'object') {
    config.__apiResultLogged = true;
  }
}

function logApiSuccess(response: any): void {
  const config = response?.config;
  if (hasApiResultLogged(config)) return;
  const context = buildApiLogContext(config, undefined, response?.status);
  apiLogger.info(`${context.api || 'API'} 请求成功`, context);
  attachApiInfoToContext(config, context, 'success');
  markApiResultLogged(config);
}

function logApiFailure(
  error: any,
  opts?: any,
  fallbackMessage?: string | null,
): void {
  const config = getErrorLogConfig(error, opts);
  if (hasApiResultLogged(config)) return;
  const response = error?.response || (error?.status ? error : undefined);
  const status = response?.status;
  const errorMessage =
    fallbackMessage ||
    extractBackendErrorMessage(response) ||
    error?.message ||
    null;
  const context = buildApiLogContext(config, opts, status, errorMessage);
  apiLogger.error(`${context.api || 'API'} 请求失败`, error, context);
  attachApiInfoToContext(config, context, 'failure');
  markApiResultLogged(config);
}

// 相同内容的错误提示在短时间窗口内只显示一次，避免并发请求同时失败时刷屏
const recentErrorMessages = new Map<string, number>();
const ERROR_DEDUPE_WINDOW = 2000;

function showErrorOnce(
  content: string,
  type: 'error' | 'warning' = 'error',
): void {
  if (!content) return;
  const now = Date.now();
  const lastShown = recentErrorMessages.get(content);
  if (lastShown && now - lastShown < ERROR_DEDUPE_WINDOW) {
    return;
  }
  recentErrorMessages.set(content, now);
  setTimeout(() => {
    if (recentErrorMessages.get(content) === now) {
      recentErrorMessages.delete(content);
    }
  }, ERROR_DEDUPE_WINDOW);
  getMessageInstance()[type](content);
}

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      const requestId = Number(
        error?.config?.__requestId ?? opts?.__requestId ?? 0,
      );
      completeInflightRequest(requestId);
      logApiFailure(error, opts);

      if (
        error?.name === 'LogoutDrainingError' ||
        error?.message === 'LOGOUT_DRAINING'
      ) {
        logger.info('登出排水中，拦截请求并短路', {
          url: error?.url ?? error?.config?.url ?? opts?.url,
        });
        throw error;
      }

      // 会话失效类错误：sessionExpiredCleanup 已处理提示和跳转，此处静默抛出即可
      if (
        error?.message === 'IDLE_TIMEOUT' ||
        error?.message === 'TOKEN_REFRESH_WINDOW_EXPIRED'
      ) {
        throw error;
      }

      // 登出排水阶段触发的取消请求，静默忽略
      if (
        error?.name === 'AbortError' ||
        error?.message?.includes?.('logout-draining')
      ) {
        logger.info('登出排水中，静默取消在途请求', {
          url: error?.config?.url ?? opts?.url,
        });
        throw error;
      }

      if (opts?.skipErrorHandler) throw error;

      // 处理401未授权错误 - 尝试刷新token
      if (error.response?.status === 401) {
        if (isLogoutDraining()) {
          throw error;
        }

        const refresh = getRefreshToken();
        const requestUrl = (opts?.url ?? error.config?.url ?? '').toString();

        // 如果是登录接口返回401，不处理，直接抛出让登录页面处理
        if (requestUrl.includes('/v1/auth/login')) {
          throw error;
        }

        // 如果是刷新token接口本身返回401，不再尝试刷新，直接跳转登录
        if (requestUrl.includes('/v1/auth/refresh')) {
          logger.error('刷新token失败，跳转登录页');
          sessionExpiredCleanup('登录已过期，请重新登录');
          throw error;
        }

        // 如果有refresh token，尝试刷新
        const access = getAccessToken();
        if (refresh && access) {
          if (isLogoutDraining()) {
            throw error;
          }

          // 闲置超时优先：超过2小时未操作，不续期，直接清理会话
          if ((error.config as any)?.__wasIdleTimeout || isIdleTimeout()) {
            logger.info('被动刷新：闲置超时，跳转登录页');
            sessionExpiredCleanup('长时间未操作，请重新登录');
            throw error;
          }

          // access token 已过续期窗口时，401 只能说明会话应结束，不能再被动刷新。
          if (isTokenExpired() && !shouldAutoRefresh()) {
            logger.info('被动刷新：access token 已超过续期窗口，跳转登录页');
            sessionExpiredCleanup('登录已过期，请重新登录');
            throw error;
          }

          // 如果已有刷新进行中（可能由请求拦截器或其他401请求发起），等待完成
          if (refreshPromise) {
            await refreshPromise.catch(() => {});
            const updatedToken = getAccessToken();
            if (updatedToken && updatedToken !== access) {
              if (!isReloadScheduled) {
                isReloadScheduled = true;
                getMessageInstance().success('会话已自动更新，正在刷新页面...');
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              }
            } else {
              // 刷新失败，token 没更新，只弹一次提示
              sessionExpiredCleanup('登录已过期，请重新登录');
            }
            throw error;
          }

          // 当前没有刷新进行中，发起刷新
          try {
            isRefreshing = true;
            refreshStartTime = Date.now();
            refreshPromise = refreshToken(refresh, access);
            const result = await refreshPromise;

            // 防御性检查：与主动刷新同源——刷新期间会话可能已被清理（登出 / sessionExpiredCleanup）。
            // 此时不能把新 token 写回，避免幽灵会话拖住后续登录。
            if (!getRefreshToken() || isLogoutDraining()) {
              logger.info('被动刷新返回时会话已结束，丢弃刷新结果');
              isRefreshing = false;
              refreshPromise = null;
              throw error;
            }

            if (result.data?.access_token && result.data?.refresh_token) {
              setTokens(
                result.data.access_token,
                result.data.refresh_token,
                result.data.expires_in,
              );
              logger.info('Token刷新成功（被动刷新）');
              isRefreshing = false;
              refreshPromise = null;

              if (!isReloadScheduled) {
                isReloadScheduled = true;
                getMessageInstance().success('会话已自动更新，正在刷新页面...');
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              }
              throw error;
            } else {
              logger.error('刷新token返回数据不完整');
              isRefreshing = false;
              refreshPromise = null;
              sessionExpiredCleanup('会话数据异常，请重新登录');
              throw error;
            }
          } catch (refreshError) {
            isRefreshing = false;
            refreshPromise = null;
            // 只有真正刷新失败才弹提示（不是上面 throw error 的 re-catch）
            if (
              refreshError !== error &&
              !(refreshError as any)?.__alreadyHandled
            ) {
              logger.error('刷新token失败', refreshError);
              sessionExpiredCleanup('登录已过期，请重新登录');
            }
            throw error;
          }
        } else {
          // 没有refresh token或access token，直接跳转登录
          logger.error('无refresh token或access token，跳转登录页');
          sessionExpiredCleanup('请先登录');
          throw error;
        }
      }

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              showErrorOnce(errorMessage || '请求失败', 'warning');
              break;
            case ErrorShowType.ERROR_MESSAGE:
              showErrorOnce(errorMessage || '请求失败');
              break;
            case ErrorShowType.NOTIFICATION:
              getNotificationInstance().open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              showErrorOnce(errorMessage || '请求失败');
          }
        }
        throw error;
      } else if (error.response) {
        // Axios 的错误：请求发出后拿到了 response 对象
        const status = error.response.status;

        // status === 0 实际上是"没拿到真正的响应"：离线 / CORS / 连接被拒 / 证书错误等
        // axios 在这类场景下仍会给出一个 status=0 的兜底 response，因此需要单独处理
        if (!status || status === 0) {
          const offlineMsg =
            typeof navigator !== 'undefined' && !navigator.onLine
              ? '网络已断开，请检查网络连接'
              : '无法连接到服务器，请稍后重试';
          showErrorOnce(offlineMsg);
          throw error;
        }

        // 401/404/409/422 不在这里弹全局提示，交由业务逻辑或上面的 401 刷新逻辑处理
        // - 401 已在上面处理
        // - 404 是正常情况，由具体业务逻辑处理
        // - 409 CONFLICT 由具体业务逻辑处理（如邀请冲突）
        // - 422 验证错误由具体业务逻辑处理，显示后端返回的具体 message
        if (
          status === 401 ||
          status === 404 ||
          status === 409 ||
          status === 422
        ) {
          throw error;
        }

        // 优先使用后端返回的业务错误文案，其次使用状态码兜底映射，最后才退回到裸状态码
        const backendMsg = extractBackendErrorMessage(error.response);
        const fallbackMsg =
          STATUS_MESSAGE_MAP[status] || `请求失败 (${status})`;
        showErrorOnce(backendMsg || fallbackMsg);
        throw error;
      } else if (error.request) {
        // 请求已发出但没收到任何响应（典型：离线、超时、请求被中止）
        const offlineMsg =
          typeof navigator !== 'undefined' && !navigator.onLine
            ? '网络已断开，请检查网络连接'
            : '服务器无响应，请稍后重试';
        showErrorOnce(offlineMsg);
        throw error;
      } else {
        // 请求配置阶段出错（极少见）
        showErrorOnce(error.message || '请求异常，请稍后重试');
        throw error;
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      (config as any).__requestStartedAt =
        (config as any).__requestStartedAt || Date.now();
      (config as any).__requestId =
        (config as any).__requestId || ++fallbackRequestSeq;

      const requestUrl = (config.url ?? '').toString();
      const isRefreshRequest = requestUrl.includes('/v1/auth/refresh');
      const isAuthRequest = isAuthAllowlistRequest(requestUrl);
      const shouldRecordActivity = !isRefreshRequest;
      const wasIdleTimeout =
        shouldRecordActivity && !isAuthRequest && isIdleTimeout();

      if (isLogoutDraining() && !isAuthAllowlistRequest(requestUrl)) {
        logger.info('请求拦截：登出排水中禁止业务请求', { url: requestUrl });
        attachApiInfoToContext(
          config,
          {
            api: `${(config.method ?? 'GET').toString().toUpperCase()} ${normalizeRequestUrl(config.url ?? '')}`,
            method: (config.method ?? 'GET').toString().toUpperCase(),
            url: normalizeRequestUrl(config.url ?? ''),
            requestId: Number((config as any).__requestId) || undefined,
            pagePath: getBrowserPagePath(),
            errorMessage: 'LOGOUT_DRAINING',
          },
          'failure',
        );
        throw new LogoutDrainingError(requestUrl);
      }

      const inflight = registerInflightRequest(requestUrl);
      if (inflight) {
        (config as any).__requestId = inflight.requestId;
        (config as any).signal = inflight.signal;
      }
      (config as any).__wasIdleTimeout = wasIdleTimeout;

      // 检查并重置异常状态
      checkAndResetState();

      // 获取访问令牌
      const token = getAccessToken();

      // 如果token存在，添加到请求头
      if (token) {
        if (wasIdleTimeout) {
          logger.info('请求拦截：闲置超时，跳转登录页');
          sessionExpiredCleanup('长时间未操作，请重新登录');
          attachApiInfoToContext(
            config,
            {
              api: `${(config.method ?? 'GET').toString().toUpperCase()} ${normalizeRequestUrl(config.url ?? '')}`,
              method: (config.method ?? 'GET').toString().toUpperCase(),
              url: normalizeRequestUrl(config.url ?? ''),
              requestId: Number((config as any).__requestId) || undefined,
              pagePath: getBrowserPagePath(),
              errorMessage: 'IDLE_TIMEOUT',
            },
            'failure',
          );
          throw new Error('IDLE_TIMEOUT');
        }

        const canAutoRefresh = shouldAutoRefresh();

        if (
          isTokenExpired() &&
          !canAutoRefresh &&
          !isAuthRequest &&
          !isRefreshRequest
        ) {
          logger.info('请求拦截：access token 已超过续期窗口，跳转登录页');
          sessionExpiredCleanup('登录已过期，请重新登录');
          attachApiInfoToContext(
            config,
            {
              api: `${(config.method ?? 'GET').toString().toUpperCase()} ${normalizeRequestUrl(config.url ?? '')}`,
              method: (config.method ?? 'GET').toString().toUpperCase(),
              url: normalizeRequestUrl(config.url ?? ''),
              requestId: Number((config as any).__requestId) || undefined,
              pagePath: getBrowserPagePath(),
              errorMessage: 'TOKEN_REFRESH_WINDOW_EXPIRED',
            },
            'failure',
          );
          throw new Error('TOKEN_REFRESH_WINDOW_EXPIRED');
        }

        // token 已过期且在续期窗口内（过期后1小时内），尝试主动刷新
        // refresh 接口本身必须跳过，否则会形成死锁（refresh 等自己完成）
        if (canAutoRefresh && !isLogoutDraining() && !isRefreshRequest) {
          const refresh = getRefreshToken();
          const access = getAccessToken();
          if (refresh && access) {
            const tokenAlreadyExpired = isTokenExpired();

            // 发起刷新（只有第一个请求会真正发起）
            if (!isRefreshing) {
              isRefreshing = true;
              refreshStartTime = Date.now();
              refreshPromise = refreshToken(refresh, access)
                .then((result) => {
                  // 防御性检查：刷新接口在飞期间用户可能已登出（clearTokens 已执行）
                  // 或会话已被 sessionExpiredCleanup 清理。此时本地已不再期望任何 token，
                  // 不能再把"过时"的刷新结果写回，否则会形成幽灵会话，导致下次登录被卡。
                  if (!getRefreshToken() || isLogoutDraining()) {
                    logger.info('主动刷新返回时会话已结束，丢弃刷新结果');
                    return;
                  }
                  if (result.data?.access_token && result.data?.refresh_token) {
                    setTokens(
                      result.data.access_token,
                      result.data.refresh_token,
                      result.data.expires_in,
                    );
                    logger.info('Token主动刷新成功');
                  } else {
                    logger.warn('主动刷新返回数据不完整');
                  }
                })
                .catch((err) => {
                  logger.error('主动刷新token失败', err);
                })
                .finally(() => {
                  isRefreshing = false;
                  refreshStartTime = null;
                  // 注意：不清除 refreshPromise，让已经拿到引用的请求能正常 await
                  setTimeout(() => {
                    refreshPromise = null;
                  }, 0);
                });
            }

            // token 已过期：阻塞当前请求，等刷新完成后用新 token 发送
            if (tokenAlreadyExpired && refreshPromise) {
              await refreshPromise;
              const newToken = getAccessToken();
              if (newToken && newToken !== token) {
                config.headers = {
                  ...config.headers,
                  Authorization: `Bearer ${newToken}`,
                };
                if (shouldRecordActivity) {
                  updateLastActiveTime();
                }
                return config;
              }
              // 刷新失败，token 没变，带旧 token 发出去让 401 处理
            }
          }
        }

        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }

      // 记录用户活跃时间（token 刷新请求本身不算用户操作）。
      // 必须在闲置判断之后记录，否则当前请求会把已超时会话误刷新为活跃状态。
      if (shouldRecordActivity) {
        updateLastActiveTime();
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const requestId = Number((response?.config as any)?.__requestId ?? 0);
      completeInflightRequest(requestId);

      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (
        data?.success === false ||
        (data as any)?.status === 'fail' ||
        (data as any)?.status === 'error'
      ) {
        const backendMsg =
          data.errorMessage ||
          (data as any).message ||
          (data as any).msg ||
          '请求失败';
        logApiFailure(response, undefined, backendMsg);
        showErrorOnce(backendMsg);
      } else {
        logApiSuccess(response);
      }
      return response;
    },
  ],
};
